// @ts-check
import { nullIfEmpty } from './object'
import { createSelector } from './selector-utils'

/**
 * This lookup is an exhaustive list of all the definition fields that are
 * considered part of the HHX.
 *
 * The string value represents whether a given key should be included in HHX
 * data when hhx is done in Xchart, elsewhere, or both.
 *
 * @type {Record<HhxFieldWithoutFinal, 'local' | 'elsewhere' | 'both'>}
 */
const hhxKeyLookup = {
  // physical
  height: 'both',
  height_raw: 'both',
  weight: 'both',
  weight_raw: 'both',
  asaClassification: 'both',
  mallampati: 'both',
  lungAuscultation: 'both',
  heartAuscultation: 'both',

  // allergies
  allergiesSummary: 'both',

  // history
  hospitalizationSummary: 'local',
  surgicalHistorySummary: 'local',
  anesthesiaHistorySummary: 'local',
  familyAnesthesiaHistorySummary: 'local',

  // open field
  healthHistorySummary: 'elsewhere',
  healthHistoryNotes: 'local',

  // medications
  medicationsText: 'local',
  medications: 'local',

  // review of systems
  systemsReview: 'local',
  systemsReviewSkipped: 'both',

  // patient measurements
  pm: 'local',

  // airway
  brodsky: 'local',
  ulbt: 'local',
  tmd: 'local',
  iid: 'local',

  // consult
  medicalConsult: 'local',
  medicalConsultNotes: 'local',
}

/** @type {HhxField[]} */
// @ts-ignore
export const caseKeysFromHhx = Object.keys(hhxKeyLookup)

/** @type {HhxField[]} */
const hhxKeysWhenDoingHhxElsewhere = caseKeysFromHhx.filter(
  key => hhxKeyLookup[key] === 'elsewhere' || hhxKeyLookup[key] === 'both'
)

/** @type {HhxField[]} */
const hhxKeysWhenDoingHhxInXchart = caseKeysFromHhx.filter(
  key => hhxKeyLookup[key] === 'local' || hhxKeyLookup[key] === 'both'
)

/**
 * This will select all the HHX data from the passed object. Even if it won't
 * ultimately be used in the report.
 *
 * If you select you've done HHX elsewhere via the systemsReviewSkipped field it
 * could contain more data than what we actually want in the report.
 *
 * @param {CaseData | HhxData} caseData
 * @returns {HhxData | null}
 */
export const selectAnyHhxDataOrNull = caseData => {
  if (!caseData) return null
  const result = {}
  for (const key of caseKeysFromHhx) {
    if (caseData.hasOwnProperty(key)) {
      result[key] = caseData[key]
    }
  }
  return nullIfEmpty(result)
}

/**
 * This will select *relevant* HHX data only following the rule that if we do
 * HHX Elsewhere, the other stuff is ignored.
 *
 * Also, if medicalConsult is false, we should not include the notes, even if
 * they exist in the data.
 *
 * @param {CaseData | HhxData} caseData
 * @returns {boolean}
 */
export const selectRelevantHhxData = createSelector(
  selectAnyHhxDataOrNull,
  hhxData => {
    if (!hhxData) return {}
    const elsewhere = !!hhxData.systemsReviewSkipped
    const keysToUse = elsewhere
      ? hhxKeysWhenDoingHhxElsewhere
      : hhxKeysWhenDoingHhxInXchart
    const result = {}
    for (const key of keysToUse) {
      if (hhxData.hasOwnProperty(key)) {
        result[key] = hhxData[key]
      }
    }
    // additional check for medicalConsultNotes
    if (!hhxData.medicalConsult) {
      delete result.medicalConsultNotes
    }
    return result
  }
)

/**
 * Returns an object of HHX data that should be cleared out based on the data within it.
 *
 * If this selector does not return null it means there are changes that should be made.
 *
 * @param {HhxData | CaseData} hhxData
 * @returns {any | null}
 */
export const selectExtraHhxDataOrNull = createSelector(
  selectAnyHhxDataOrNull,
  selectRelevantHhxData,
  (allHhx, relevantHhx) => {
    if (!allHhx) return null
    const changes = {}
    for (const key in allHhx) {
      if (!relevantHhx.hasOwnProperty(key)) {
        changes[key] = allHhx[key]
      }
    }
    return nullIfEmpty(changes)
  }
)

/**
 * @param {CaseData | HhxData} caseData
 * @returns {boolean}
 */
export const selectContainsAnyHhxData = createSelector(
  selectAnyHhxDataOrNull,
  x => !!x
)

/**
 * This helps us combine data from the case and hhx data in a way that matches
 * our business rules. Specifically any time we have any HHX data in the case
 * data we want to just use it and completely ignore the HHX data.
 *
 * This is how we can let people override the HHX data with the case data.
 *
 * @param {CaseData} caseData
 * @param {HhxData} [hhxData]
 * @returns {CaseData}
 */
export const mergeCaseAndHhx = (caseData, hhxData) => {
  if (selectContainsAnyHhxData(caseData)) {
    return caseData
  }
  /** @type {{ [key: string]: any }} */
  const toReturn = { ...caseData }
  const relevantHhxData = selectRelevantHhxData(hhxData || {})
  /**
   * We want to loop through *all* the keys, but only include from relevant keys
   * because we also want to make sure the non-relevant HHX keys are removed
   * from the returned object.
   */
  for (const key of caseKeysFromHhx) {
    if (relevantHhxData.hasOwnProperty(key)) {
      toReturn[key] = relevantHhxData[key]
    } else {
      // we delete the key from the returned combined case data
      // because we want to grab one or the other, not mix them.
      delete toReturn[key]
    }
  }
  return toReturn
}
